/**
 * Tenant Validation Service
 *
 * Validates the current tenant on application startup.
 * Redirects to error page if tenant is invalid.
 */

import axios from 'axios';

/**
 * Extract tenant slug from current hostname
 * @returns {string|null} Tenant slug or null if not multi-tenant
 */
export function getTenantFromHostname() {
  const { hostname } = window.location;

  // Check for localhost development
  if (hostname.endsWith('.localhost')) {
    const parts = hostname.split('.');
    if (parts.length >= 2) {
      const subdomain = parts[0];
      // Skip 'admin' subdomain (platform admin portal)
      if (subdomain !== 'admin') {
        return subdomain;
      }
    }
  }

  // Check for production domains (e.g., tenant.example.com)
  const parts = hostname.split('.');
  if (parts.length >= 3) {
    const subdomain = parts[0];
    // Skip 'admin', 'www', 'app' subdomains
    if (!['admin', 'www', 'app'].includes(subdomain)) {
      return subdomain;
    }
  }

  return null;
}

/**
 * Check if current subdomain is the admin portal
 * @returns {boolean}
 */
export function isAdminPortal() {
  const { hostname } = window.location;

  if (hostname.endsWith('.localhost')) {
    return hostname.startsWith('admin.');
  }

  const parts = hostname.split('.');
  return parts.length >= 3 && parts[0] === 'admin';
}

/**
 * Check if accessing bare domain (no subdomain)
 * @returns {boolean}
 */
export function isBareDomain() {
  const { hostname } = window.location;

  // localhost without subdomain
  if (hostname === 'localhost') {
    return true;
  }

  // For .localhost domains, we should have a subdomain
  if (hostname.endsWith('.localhost')) {
    return false; // Has subdomain like tenant.localhost
  }

  // Production: check if it's a bare domain (no subdomain)
  const parts = hostname.split('.');
  // domain.com = 2 parts = bare domain
  // www.domain.com or tenant.domain.com = 3+ parts = has subdomain
  return parts.length <= 2;
}

/**
 * Validate tenant with backend
 * @param {string} tenantSlug - Tenant slug to validate
 * @returns {Promise<{valid: boolean, error?: string, message?: string}>}
 */
export async function validateTenant(tenantSlug) {
  try {
    const response = await axios.get(
      `/content-management-api/public/tenant/${tenantSlug}/validate`,
      { timeout: 10000 },
    );

    if (response.data?.success && response.data?.valid) {
      return { valid: true };
    }

    return {
      valid: false,
      error: response.data?.error || 'UNKNOWN_ERROR',
      message: response.data?.message || 'Tenant validation failed',
    };
  } catch (error) {
    // Network error or 4xx/5xx response
    if (error.response?.data) {
      return {
        valid: false,
        error: error.response.data.error || 'VALIDATION_ERROR',
        message: error.response.data.message || 'Failed to validate tenant',
      };
    }

    // Network error - don't block the app, let it proceed
    console.warn('Tenant validation failed due to network error:', error.message);
    return { valid: true }; // Optimistic - let the app try to load
  }
}

/**
 * Redirect to tenant error page
 * @param {string} errorType - Error type code
 * @param {string} tenantSlug - Tenant slug
 * @param {string} message - Custom error message
 */
export function redirectToTenantError(errorType, tenantSlug, message = '') {
  const params = new URLSearchParams();
  params.set('error', errorType);
  if (tenantSlug) params.set('tenant', tenantSlug);
  if (message) params.set('message', message);

  // Use window.location to force a full page navigation
  window.location.href = `/tenant-error?${params.toString()}`;
}

/**
 * Redirect to no-domain page (bare domain access)
 */
export function redirectToNoDomain() {
  // Use window.location to force a full page navigation
  window.location.href = '/no-domain';
}

/**
 * Main validation function to call on app startup
 * @param {boolean} multiTenantEnabled - Whether multi-tenant mode is enabled
 * @returns {Promise<boolean>} True if validation passed, false if redirected
 */
export async function validateCurrentTenant(multiTenantEnabled) {
  // Skip validation if not in multi-tenant mode
  if (!multiTenantEnabled) {
    return true;
  }

  // Skip validation for admin portal
  if (isAdminPortal()) {
    return true;
  }

  // Skip if already on error page or no-domain page
  if (window.location.pathname === '/tenant-error'
      || window.location.pathname === '/no-domain') {
    return true;
  }

  // Check if accessing bare domain (no subdomain)
  if (isBareDomain()) {
    redirectToNoDomain();
    return false;
  }

  // Get tenant from hostname
  const tenantSlug = getTenantFromHostname();

  // No tenant subdomain - should not happen at this point, but fallback
  if (!tenantSlug) {
    redirectToNoDomain();
    return false;
  }

  // Validate tenant
  const result = await validateTenant(tenantSlug);

  if (!result.valid) {
    redirectToTenantError(result.error, tenantSlug, result.message);
    return false;
  }

  return true;
}

export default {
  getTenantFromHostname,
  isAdminPortal,
  isBareDomain,
  validateTenant,
  redirectToTenantError,
  redirectToNoDomain,
  validateCurrentTenant,
};
